import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle } from '../theme';

/**
 * SmartRecordModal — Voice AI that listens to doctor-patient conversation
 * and auto-generates treatment plans, prescriptions, appointments, recalls, etc.
 *
 * Props:
 *   API_BASE, patientId, patientName, treatmentTypes, onClose
 *   onCreatePlan(plan)          — callback to create treatment plan
 *   onCreateAppointment(appt)   — callback
 *   onCreateRecall(recall)      — callback
 *   onUpdateHistory(conditions) — callback to update patient medical history
 */
const SmartRecordModal = ({ API_BASE, patientId, patientName, treatmentTypes, onClose,
  onCreatePlan, onCreateAppointment, onCreateRecall, onUpdateHistory }) => {

  const [stage, setStage] = useState('record'); // record | processing | results
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [results, setResults] = useState(null);
  const [approved, setApproved] = useState({}); // { treatments_0: true, medicines_1: true, ... }
  const [saving, setSaving] = useState(false);

  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const finalTranscriptRef = useRef('');

  const startRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Use Chrome browser for voice recording'); return; }
    const recognition = new SR();
    recognition.lang = 'hi-IN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    finalTranscriptRef.current = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscriptRef.current += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setTranscript(finalTranscriptRef.current + interim);
    };

    recognition.onerror = (e) => {
      if (e.error === 'aborted' || e.error === 'not-allowed') {
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        try { recognition.start(); } catch(e) {
          setTimeout(() => { if (isRecordingRef.current) try { recognition.start(); } catch(e2) {} }, 200);
        }
      }
    };

    recognitionRef.current = recognition;
    isRecordingRef.current = true;
    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    setTranscript(finalTranscriptRef.current);
  };

  const processTranscript = async () => {
    if (!transcript.trim()) return alert('Record a conversation first');
    setStage('processing');
    try {
      const res = await axios.post(`${API_BASE}/smart-plan/process`, { transcript, patientName });
      setResults(res.data);
      setStage('results');
    } catch (e) {
      alert('Processing failed');
      setStage('record');
    }
  };

  const toggleApproval = (key) => {
    setApproved(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveAll = async () => {
    if (!results) return;
    setSaving(true);
    let saved = [];

    // Save approved treatments as plans
    for (let i = 0; i < (results.treatments || []).length; i++) {
      if (approved[`tt_${i}`]) {
        const t = results.treatments[i];
        const tt = (treatmentTypes || []).find(x => x.name === t.treatment);
        try {
          await axios.post(`${API_BASE}/treatments/plan`, {
            patientId,
            treatmentName: t.treatment,
            toothNumber: t.tooth || '',
            price: tt?.defaultPrice || 0,
            units: tt?.defaultUnits || 1,
            gstPercent: tt?.gstPercent || 0,
            status: 'planned',
          });
          saved.push(`Treatment Plan: ${t.treatment}`);
        } catch(e) {}
      }
    }

    // Save approved appointment
    if (results.nextAppointment && approved['appt']) {
      try {
        await axios.post(`${API_BASE}/appointments`, {
          patientName,
          date: results.nextAppointment.date,
          time: '09:00',
          treatment: (results.treatments || []).map(t => t.treatment).join(', '),
          status: 'Scheduled',
        });
        saved.push(`Appointment: ${results.nextAppointment.date}`);
      } catch(e) {}
    }

    // Save approved recall
    if (results.recall && approved['recall']) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + results.recall.days);
      try {
        await axios.post(`${API_BASE}/recalls`, {
          patientId,
          type: results.recall.type,
          dueDate: dueDate.toISOString(),
        });
        saved.push(`Recall: ${results.recall.type}`);
      } catch(e) {}
    }

    // Save voice note with medical extraction
    try {
      const medRes = await axios.post(`${API_BASE}/notes/extract-medical`, { text: transcript });
      await axios.post(`${API_BASE}/notes`, {
        patientId,
        title: `Smart Record — ${new Date().toLocaleDateString('en-IN')}`,
        text: transcript,
        medicalText: medRes.data.medicalText || '',
        type: 'voice',
      });
      saved.push('Voice Note saved');
    } catch(e) {}

    setSaving(false);
    alert(`Saved:\n${saved.join('\n') || 'Nothing was approved'}`);
    if (saved.length > 0) onClose();
  };

  const fmtINR = n => `₹${(n||0).toLocaleString('en-IN')}`;

  const SuggestionCard = ({ title, icon, color, itemKey, children }) => {
    const isApproved = approved[itemKey];
    return (
      <div style={{
        border: `2px solid ${isApproved ? color : theme.border}`,
        borderRadius: '12px', padding: '14px', marginBottom: '10px',
        backgroundColor: isApproved ? color + '08' : 'white',
        transition: 'all 0.2s',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}>{icon}</span>
            <strong style={{ color: theme.navy, fontSize: '0.9rem' }}>{title}</strong>
          </div>
          <button onClick={() => toggleApproval(itemKey)}
            style={{
              padding: '6px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontWeight: '700', fontSize: '0.82rem',
              backgroundColor: isApproved ? color : '#e9ecef',
              color: isApproved ? 'white' : '#555',
            }}>
            {isApproved ? '✓ Approved' : 'Approve'}
          </button>
        </div>
        {children}
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '16px',
          width: '900px', maxWidth: '95vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>

        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #eee',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, color: theme.navy }}>🎤 Smart Record — AI Treatment Planner</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: theme.textMuted }}>Patient: {patientName}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#aaa' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── STAGE 1: RECORD ── */}
          {stage === 'record' && (
            <div>
              <div style={{ textAlign: 'center', padding: '20px 0 24px' }}>
                <div style={{
                  width: '100px', height: '100px', borderRadius: '50%', margin: '0 auto 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isRecording
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                  boxShadow: isRecording ? '0 0 0 8px rgba(239,68,68,0.2)' : `0 4px 14px rgba(0,181,184,0.3)`,
                  cursor: 'pointer',
                  animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                }} onClick={isRecording ? stopRecording : startRecording}>
                  <span style={{ fontSize: '2.5rem', color: 'white' }}>{isRecording ? '⏹' : '🎤'}</span>
                </div>
                <h3 style={{ color: theme.navy, margin: '0 0 4px' }}>
                  {isRecording ? 'Recording... Speak now' : 'Click to start recording'}
                </h3>
                <p style={{ color: theme.textMuted, fontSize: '0.85rem', margin: 0 }}>
                  {isRecording ? 'AI is listening to your conversation with the patient' : 'Record your conversation — AI will extract treatment plans, medicines, and more'}
                </p>
              </div>

              {/* Live transcript */}
              {transcript && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '0.78rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Live Transcript ({transcript.split(' ').length} words)
                  </label>
                  <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
                    style={{ ...inputStyle, height: '150px', resize: 'vertical', fontSize: '0.88rem', lineHeight: '1.6' }} />
                </div>
              )}

              {!isRecording && transcript.trim() && (
                <div style={{ textAlign: 'center' }}>
                  <button onClick={processTranscript}
                    style={{
                      padding: '14px 40px',
                      background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                      color: 'white', border: 'none', borderRadius: '12px',
                      cursor: 'pointer', fontWeight: '700', fontSize: '1rem',
                      boxShadow: '0 4px 14px rgba(0,181,184,0.3)',
                    }}>
                    🤖 Process with AI
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── STAGE 2: PROCESSING ── */}
          {stage === 'processing' && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px', animation: 'pulse 1s infinite' }}>🤖</div>
              <h3 style={{ color: theme.navy }}>AI is analyzing the conversation...</h3>
              <p style={{ color: theme.textMuted }}>Extracting treatments, medicines, diagnoses, and more</p>
            </div>
          )}

          {/* ── STAGE 3: RESULTS ── */}
          {stage === 'results' && results && (
            <div>
              {/* Confidence */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                backgroundColor: results.confidence >= 60 ? '#f0fdf4' : '#fef3c7',
                borderRadius: '10px', marginBottom: '16px',
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: results.confidence >= 60 ? theme.success : theme.warning,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: '800', fontSize: '1.1rem',
                }}>{results.confidence}%</div>
                <div>
                  <div style={{ fontWeight: '700', color: theme.navy }}>AI Confidence: {results.confidence}%</div>
                  <div style={{ fontSize: '0.82rem', color: theme.textMuted }}>
                    Found: {results.treatments?.length || 0} treatments, {results.medicines?.length || 0} medicines,
                    {results.diagnoses?.length || 0} diagnoses, {results.detectedTeeth?.length || 0} teeth
                  </div>
                </div>
              </div>

              {/* Chief Complaint */}
              {results.chiefComplaint && (
                <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '14px' }}>
                  <strong style={{ color: theme.navy }}>Chief Complaint:</strong> {results.chiefComplaint}
                </div>
              )}

              {/* Teeth detected */}
              {results.detectedTeeth?.length > 0 && (
                <div style={{ padding: '10px 14px', backgroundColor: '#e8f4fd', borderRadius: '8px', marginBottom: '14px' }}>
                  <strong style={{ color: theme.navy }}>Teeth Detected:</strong> {results.detectedTeeth.map(t => `#${t}`).join(', ')}
                </div>
              )}

              {/* Treatment Plans */}
              {(results.treatments || []).map((t, i) => {
                const tt = (treatmentTypes || []).find(x => x.name === t.treatment);
                return (
                  <SuggestionCard key={`tt_${i}`} itemKey={`tt_${i}`} title={`Treatment Plan: ${t.treatment}`}
                    icon="💉" color={theme.success}>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: theme.textMuted }}>
                      {t.tooth && <span>Tooth: <strong>#{t.tooth}</strong></span>}
                      {tt && <span>Price: <strong>{fmtINR(tt.defaultPrice)}</strong></span>}
                    </div>
                  </SuggestionCard>
                );
              })}

              {/* Diagnoses */}
              {(results.diagnoses || []).map((d, i) => (
                <SuggestionCard key={`dx_${i}`} itemKey={`dx_${i}`} title={`Diagnosis: ${d.diagnosis}`}
                  icon="🩺" color={theme.info}>
                  <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>Finding: {d.finding}</div>
                </SuggestionCard>
              ))}

              {/* Medicines */}
              {(results.medicines || []).length > 0 && (
                <SuggestionCard itemKey="meds" title={`Medicines (${results.medicines.length})`}
                  icon="💊" color={theme.purple}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>MEDICINE</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>DOSAGE</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>DURATION</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '0.74rem', color: theme.textMuted }}>INSTRUCTIONS</th>
                    </tr></thead>
                    <tbody>{results.medicines.map((m, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '6px 8px', fontWeight: '600', fontSize: '0.85rem' }}>{m.name}</td>
                        <td style={{ padding: '6px 8px', fontSize: '0.85rem' }}>{m.dosage}</td>
                        <td style={{ padding: '6px 8px', fontSize: '0.85rem' }}>{m.duration}</td>
                        <td style={{ padding: '6px 8px', fontSize: '0.82rem', color: theme.textMuted }}>{m.instructions}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </SuggestionCard>
              )}

              {/* Next Appointment */}
              {results.nextAppointment && (
                <SuggestionCard itemKey="appt" title={`Next Appointment: ${results.nextAppointment.label}`}
                  icon="📅" color={theme.warning}>
                  <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>
                    Date: <strong>{new Date(results.nextAppointment.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                    ({results.nextAppointment.days} days from today)
                  </div>
                </SuggestionCard>
              )}

              {/* Recall */}
              {results.recall && (
                <SuggestionCard itemKey="recall" title={`Recall: ${results.recall.type}`}
                  icon="🔔" color={theme.pink}>
                  <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>
                    Due in: <strong>{results.recall.label}</strong>
                  </div>
                </SuggestionCard>
              )}

              {/* Medical History Updates */}
              {(results.historyUpdates || []).length > 0 && (
                <SuggestionCard itemKey="history" title={`Medical History Update`}
                  icon="📋" color={theme.danger}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {results.historyUpdates.map((h, i) => (
                      <span key={i} style={{ padding: '4px 12px', backgroundColor: theme.danger + '15', color: theme.danger, borderRadius: '12px', fontSize: '0.82rem', fontWeight: '600' }}>{h}</span>
                    ))}
                  </div>
                </SuggestionCard>
              )}

              {/* Estimate */}
              {(results.treatments || []).length > 0 && (
                <div style={{ padding: '12px 16px', backgroundColor: '#f0fdf4', borderRadius: '10px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: theme.navy }}>Estimated Cost:</span>
                  <span style={{ fontSize: '1.3rem', fontWeight: '700', color: theme.success }}>
                    {fmtINR(results.treatments.reduce((sum, t) => {
                      const tt = (treatmentTypes || []).find(x => x.name === t.treatment);
                      return sum + (tt?.defaultPrice || 0);
                    }, 0))}
                  </span>
                </div>
              )}

              {/* Back + Save buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                <button onClick={() => { setStage('record'); setResults(null); setApproved({}); }}
                  style={{ padding: '10px 24px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                  ← Re-record
                </button>
                <button onClick={handleSaveAll} disabled={saving}
                  style={{
                    padding: '12px 36px',
                    background: `linear-gradient(135deg, ${theme.success} 0%, #059669 100%)`,
                    color: 'white', border: 'none', borderRadius: '10px',
                    cursor: 'pointer', fontWeight: '700', fontSize: '1rem',
                    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                  }}>
                  {saving ? 'Saving...' : `✓ Save Approved Items (${Object.values(approved).filter(Boolean).length})`}
                </button>
              </div>
            </div>
          )}
        </div>

        <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.05)} }`}</style>
      </div>
    </div>
  );
};

export default SmartRecordModal;
